import { Redux } from '../..'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  BookingJournalItem,
  GygEonFacts,
  GygEonFactsQueryOptions,
  UpdateDailyStockPinnedProductsPayload
} from '@/src/types/apps/gygEonFacts'
import { normalizeDailyStockPinnedProductIds } from '@/src/views/components/end-of-night/bookingJournal.constants'

interface EonFactsInitialState {
  eonFacts: GygEonFacts[]
  bookingJournalItems: BookingJournalItem[]
  dailyStockPinnedProductIds: number[]
  loading: boolean
  success: string | null
  error: string | null
}

const initialState: EonFactsInitialState = {
  eonFacts: [],
  bookingJournalItems: [],
  dailyStockPinnedProductIds: [],
  loading: false,
  success: null,
  error: null
}

interface EonFactsResponse {
  data: GygEonFacts[]
  bookingJournalItems: BookingJournalItem[]
  dailyStockPinnedProductIds: number[]
}

function extractBookingJournalItems(payload: Record<string, unknown>): BookingJournalItem[] {
  const meta = payload.meta && typeof payload.meta === 'object' ? (payload.meta as Record<string, unknown>) : null

  const candidates = [payload.bookingJournal, meta?.bookingJournal]

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue

    const items = (candidate as Record<string, unknown>).items
    if (Array.isArray(items)) {
      return items as BookingJournalItem[]
    }
  }

  return []
}

function extractDailyStockPinnedProductIds(payload: Record<string, unknown>): number[] {
  const meta = payload.meta && typeof payload.meta === 'object' ? (payload.meta as Record<string, unknown>) : null
  const candidates = [payload.dailyStockPinnedProductIds, meta?.dailyStockPinnedProductIds]

  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue

    return normalizeDailyStockPinnedProductIds(candidate)
  }

  return []
}

function normalizeEonFactsPayload(raw: unknown): EonFactsResponse {
  if (raw == null) {
    return { data: [], bookingJournalItems: [], dailyStockPinnedProductIds: [] }
  }
  const payload = typeof raw === 'object' ? (raw as Record<string, unknown>) : null
  if (!payload) {
    return { data: [], bookingJournalItems: [], dailyStockPinnedProductIds: [] }
  }

  const data = Array.isArray(payload.data) ? (payload.data as GygEonFacts[]) : []
  const bookingJournalItems = extractBookingJournalItems(payload)
  const dailyStockPinnedProductIds = extractDailyStockPinnedProductIds(payload)

  return { data, bookingJournalItems, dailyStockPinnedProductIds }
}

function normalizePinnedProductsPayload(raw: unknown): number[] {
  if (raw == null || typeof raw !== 'object') return []

  const payload = raw as Record<string, unknown>
  const nested = payload.data && typeof payload.data === 'object' ? (payload.data as Record<string, unknown>) : null
  const candidates = [
    payload.productIds,
    nested?.productIds,
    payload.dailyStockPinnedProductIds,
    nested?.dailyStockPinnedProductIds
  ]

  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue

    return normalizeDailyStockPinnedProductIds(candidate)
  }

  return []
}

export const getEonFactsInTwoDates = createAsyncThunk<EonFactsResponse, GygEonFactsQueryOptions, Redux>(
  'gygEonFacts/getEonFactsInTwoDates',
  async (gygEonFactsQueryOptions, { extra }) => {
    const { restaurantId, startDate, endDate } = gygEonFactsQueryOptions

    const { data: responseData } = await extra.httpClient.get(`restaurants/${restaurantId}/eon-facts`, {
      params: {
        startDate,
        endDate
      }
    })

    return normalizeEonFactsPayload(responseData)
  }
)

export const update = createAsyncThunk<EonFactsResponse, any, Redux>('gygEonFacts/update', async (data, { extra }) => {
  const { httpClient } = extra

  const { data: responseData } = await httpClient.patch(`restaurants/${data.restaurantId}/eon-facts`, data)

  return normalizeEonFactsPayload(responseData)
})

export const updateDailyStockPinnedProducts = createAsyncThunk<number[], UpdateDailyStockPinnedProductsPayload, Redux>(
  'gygEonFacts/updateDailyStockPinnedProducts',
  async ({ restaurantId, productIds }, { extra }) => {
    const { data: responseData } = await extra.httpClient.patch(
      `restaurants/${restaurantId}/eon-facts/daily-stock-pinned-products`,
      { productIds }
    )

    return normalizeDailyStockPinnedProductIds(normalizePinnedProductsPayload(responseData))
  }
)

const gygEonFactsSlice = createSlice({
  name: 'getAllEonFacts',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getEonFactsInTwoDates.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getEonFactsInTwoDates.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.eonFacts = Array.isArray(action.payload?.data) ? action.payload.data : []
        state.bookingJournalItems = Array.isArray(action.payload?.bookingJournalItems)
          ? action.payload.bookingJournalItems
          : []
        state.dailyStockPinnedProductIds = Array.isArray(action.payload?.dailyStockPinnedProductIds)
          ? action.payload.dailyStockPinnedProductIds
          : []
      })
      .addCase(getEonFactsInTwoDates.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch eon goals targets'
      })
      .addCase(update.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(update.fulfilled, state => {
        state.loading = false
        state.success = 'Daily count form data updated successfully'
      })
      .addCase(update.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update eon facts'
        state.success = null
      })
      .addCase(updateDailyStockPinnedProducts.pending, state => {
        state.error = null
      })
      .addCase(updateDailyStockPinnedProducts.fulfilled, (state, action) => {
        state.dailyStockPinnedProductIds = action.payload
      })
      .addCase(updateDailyStockPinnedProducts.rejected, (state, action) => {
        state.error = action.error.message ?? 'Failed to update daily stock products'
      })
  }
})

export default gygEonFactsSlice.reducer
